'use client';

import { useTranslations } from 'next-intl';
import { usePathname, useRouter } from '@/i18n/navigation';

const LOCALE_LABELS: Record<string, string> = {
  de: 'Deutsch',
  fr: 'Français',
};

interface LocaleOverlayProps {
  locales: string[];
  currentLocale: string;
  isOpen: boolean;
  onClose: () => void;
}

export const LocaleOverlay = ({
  locales,
  currentLocale,
  isOpen,
  onClose,
}: LocaleOverlayProps) => {
  const t = useTranslations('header');
  const router = useRouter();
  const pathname = usePathname();

  const handleSwitch = (locale: string) => {
    onClose();
    const search = window.location.search;
    router.replace(`${pathname}${search}`, { locale });
  };

  /*
   * Present in the DOM whether or not it is open, hidden with the `hidden`
   * attribute (SH-528) — the same treatment the main and section menus get.
   * These are the brand's locale alternates, which a crawler should be able to
   * see without opening a switcher.
   */
  return (
    <nav
      aria-label={t('selectLanguage')}
      hidden={!isOpen}
      aria-hidden={!isOpen}
      className="bg-secondary-lighter text-foreground relative z-10 shadow-lg lg:bg-white"
    >
      <div className="mx-auto max-w-6xl px-0 py-2 md:px-4 lg:px-9 lg:py-8">
        <ul className="list-none lg:grid lg:grid-cols-3 lg:gap-x-8">
          {locales.map((locale) => {
            const isActive = locale === currentLocale;
            return (
              <li
                key={locale}
                className="border-card-border border-b last:border-b-0 lg:border-none"
              >
                <button
                  onClick={() => handleSwitch(locale)}
                  disabled={isActive}
                  className={[
                    'flex w-full items-center py-3 text-base font-medium transition-colors md:py-4',
                    isActive
                      ? 'text-primary cursor-default'
                      : 'hover:text-primary cursor-pointer',
                  ].join(' ')}
                >
                  {LOCALE_LABELS[locale] ?? locale.toUpperCase()}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
};
